// src/pages/Reports.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Form, Badge, Nav, Tab } from 'react-bootstrap';
import { Calendar, Car, DollarSign, Trophy } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmt    = v => `PKR ${Number(v||0).toLocaleString()}`;
const fmtHrs = m => { if (!m) return '0h'; return `${Math.floor(m/60)}h ${m%60}m`; };

function isoMonthStart() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; }
function isoToday()      { return new Date().toISOString().slice(0,10); }

function exportCSV(data, filename) {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const rows = [keys.join(','), ...data.map(r => keys.map(k => JSON.stringify(r[k]??'')).join(','))];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
  a.download = filename; a.click();
}

function TableSkeleton({ rows = 5, cols = 6 }) {
  return (
    <Table responsive className="align-middle" style={{ fontSize: 13 }}>
      <thead>
        <tr>
          {Array.from({ length: cols }).map((_, i) => (
            <th key={i}><div className="crm-skeleton-pulse" style={{ width: '80%', height: 16 }} /></th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r}>
            {Array.from({ length: cols }).map((_, c) => (
              <td key={c}><div className="crm-skeleton-pulse" style={{ width: c === 0 ? '60%' : '85%', height: 18 }} /></td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function AttendanceReport({ user, employees }) {
  const [data,  setData]  = useState([]);
  const [start, setStart] = useState(isoMonthStart());
  const [end,   setEnd]   = useState(isoToday());
  const [empId, setEmpId] = useState('');
  const [loading,setLoading]=useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start, end, limit: 500 });
      if (empId) params.set('employeeId', empId);
      const res = await api.get(`/attendance/history?${params}`);
      setData(Array.isArray(res.data?.records) ? res.data.records : []);
    } catch { setData([]); } finally { setLoading(false); }
  }, [start, end, empId]);

  useEffect(() => { load(); }, [load]);

  const visEmp = user?.role === 'Admin' ? employees : employees.filter(e => e.manager === user?.name || e.email === user?.email);

  return (
    <div>
      <div className="row g-3 mb-3 align-items-end">
        {user?.role !== 'Agent' && (
          <div className="col-md-3">
            <Form.Label className="small fw-semibold">Employee</Form.Label>
            <Form.Select size="sm" value={empId} onChange={e => setEmpId(e.target.value)}>
              <option value="">All Employees</option>
              {visEmp.map(e => <option key={e.id||e._id} value={e.id||e._id}>{e.name}</option>)}
            </Form.Select>
          </div>
        )}
        <div className="col-md-2"><Form.Label className="small fw-semibold">Start</Form.Label><Form.Control size="sm" type="date" value={start} onChange={e => setStart(e.target.value)} /></div>
        <div className="col-md-2"><Form.Label className="small fw-semibold">End</Form.Label><Form.Control size="sm" type="date" value={end} onChange={e => setEnd(e.target.value)} /></div>
        <div className="col-md-2">
          <button className="btn btn-outline-secondary btn-sm mt-3" onClick={() => exportCSV(data, 'attendance-report.csv')}>Export CSV</button>
        </div>
      </div>
      {loading ? <TableSkeleton rows={5} cols={4} /> : (
        <Table hover responsive className="align-middle" style={{ fontSize: 13 }}>
          <thead><tr><th>Date</th><th>Employee</th><th>Working Hours</th><th>Logout Type</th></tr></thead>
          <tbody>
            {data.length === 0 && <tr><td colSpan={4} className="text-center text-muted py-4">No data.</td></tr>}
            {data.map(r => (
              <tr key={r.id}><td>{new Date(r.date).toLocaleDateString('en-GB')}</td><td>{r.employeeName||'—'}</td><td>{fmtHrs(r.workingMins)}</td><td><Badge bg={r.logoutReason==='auto'?'danger':'success'}>{r.logoutReason||'manual'}</Badge></td></tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

function SalesReport({ user, brands }) {
  const [data,    setData]    = useState([]);
  const [brandId, setBrandId] = useState('');
  const [start,   setStart]   = useState(isoMonthStart());
  const [end,     setEnd]     = useState(isoToday());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const endpoint = user?.role === 'Admin' ? '/sales/all' : '/sales/team';
    api.get(endpoint).then(r => {
      if (user?.role === 'Admin') setData(r.data?.byBrand || []);
      else setData(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user?.role]);

  return (
    <div>
      <div className="row g-3 mb-3 align-items-end">
        <div className="col-md-3">
          <Form.Label className="small fw-semibold">Brand</Form.Label>
          <Form.Select size="sm" value={brandId} onChange={e => setBrandId(e.target.value)}>
            <option value="">All Brands</option>
            {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Form.Select>
        </div>
        <div className="col-md-2"><Form.Label className="small fw-semibold">Start</Form.Label><Form.Control size="sm" type="date" value={start} onChange={e => setStart(e.target.value)} /></div>
        <div className="col-md-2"><Form.Label className="small fw-semibold">End</Form.Label><Form.Control size="sm" type="date" value={end} onChange={e => setEnd(e.target.value)} /></div>
      </div>
      {loading ? <TableSkeleton rows={5} cols={5} /> : (
        user?.role === 'Admin' ? (
          <div className="row g-3">
            {data.map(b => (
              <div className="col-md-4" key={b.id}>
                <Card className="border-0 shadow-sm" style={{ borderLeft: `4px solid ${b.color}` }}>
                  <Card.Body><div className="fw-semibold" style={{ color: b.color }}>{b.brandName}</div>
                  <div className="small text-muted">{b.carsSold} cars · {fmt(b.revenue)} · {fmt(b.profit)} profit</div></Card.Body>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <Table hover responsive className="align-middle" style={{ fontSize: 13 }}>
            <thead><tr><th>Agent</th><th>Cars (Month)</th><th>Lifetime</th><th>Revenue</th><th>Profit</th></tr></thead>
            <tbody>
              {data.map(a => <tr key={a.agentId}><td>{a.agentName}</td><td>{a.monthlyCarsSold}</td><td>{a.lifetimeCarsSold}</td><td>{fmt(a.totalRevenue)}</td><td>{fmt(a.totalProfit)}</td></tr>)}
            </tbody>
          </Table>
        )
      )}
    </div>
  );
}

function PayrollReport({ user, employees }) {
  const now = new Date();
  const [data,   setData]   = useState([]);
  const [month,  setMonth]  = useState(now.getMonth()+1);
  const [year,   setYear]   = useState(now.getFullYear());
  const [loading,setLoading]= useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/commissions/payroll-team?month=${month}&year=${year}`)
      .then(r => setData(Array.isArray(r.data) ? r.data : []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [month, year]);

  const total = data.reduce((s,r) => s + Number(r.totalEarnings||0), 0);

  return (
    <div>
      <div className="d-flex gap-2 mb-3">
        <Form.Select size="sm" style={{ width: 120 }} value={month} onChange={e => setMonth(Number(e.target.value))}>
          {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
        </Form.Select>
        <Form.Select size="sm" style={{ width: 90 }} value={year} onChange={e => setYear(Number(e.target.value))}>
          {[now.getFullYear(), now.getFullYear()-1].map(y => <option key={y} value={y}>{y}</option>)}
        </Form.Select>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => exportCSV(data, 'payroll-report.csv')}>Export CSV</button>
      </div>
      {loading ? <TableSkeleton rows={5} cols={7} /> : (
        <Table hover responsive className="align-middle" style={{ fontSize: 13 }}>
          <thead><tr><th>Name</th><th>Fixed</th><th>Commission</th><th>Bonuses</th><th>Deductions</th><th>Total</th><th>Status</th></tr></thead>
          <tbody>
            {data.map(r => <tr key={r.employeeId}><td className="fw-semibold">{r.name}</td><td>{fmt(r.fixedSalary)}</td><td>{fmt(r.commission)}</td><td>{fmt(r.bonuses)}</td><td className="text-danger">{fmt(r.deductions)}</td><td className="fw-bold text-success">{fmt(r.totalEarnings)}</td><td><Badge bg={r.paymentStatus==='paid'?'success':'warning'}>{r.paymentStatus}</Badge></td></tr>)}
          </tbody>
          {data.length > 0 && <tfoot><tr><td colSpan={5} className="fw-bold">Total Payroll</td><td className="fw-bold text-success">{fmt(total)}</td><td></td></tr></tfoot>}
        </Table>
      )}
    </div>
  );
}

function PerformanceReport() {
  const now = new Date();
  const [data,   setData]  = useState([]);
  const [month,  setMonth] = useState(now.getMonth()+1);
  const [year,   setYear]  = useState(now.getFullYear());
  const [loading,setLoading]=useState(false);

  useEffect(() => {
    setLoading(true);
    api.get('/sales/team').then(r => {
      const sorted = (Array.isArray(r.data)?r.data:[]).sort((a,b) => (b.monthlyCarsSold||0)-(a.monthlyCarsSold||0));
      setData(sorted);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, [month, year]);

  return (
    <div>
      <div className="d-flex gap-2 mb-4">
        <Form.Select size="sm" style={{ width: 120 }} value={month} onChange={e => setMonth(Number(e.target.value))}>
          {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
        </Form.Select>
        <Form.Select size="sm" style={{ width: 90 }} value={year} onChange={e => setYear(Number(e.target.value))}>
          {[now.getFullYear(), now.getFullYear()-1].map(y => <option key={y} value={y}>{y}</option>)}
        </Form.Select>
      </div>

      {loading ? <TableSkeleton rows={5} cols={7} /> : (
        <>
          {/* Top 3 */}
          {data.slice(0,3).length > 0 && (
            <div className="row g-3 mb-4">
              {data.slice(0,3).map((a,i) => (
                <div className="col-md-4" key={a.agentId}>
                  <Card className="border-0 shadow-sm text-center py-3" style={{ borderTop: `3px solid ${['#f59e0b','#94a3b8','#cd7c2f'][i]}` }}>
                    <div className="d-flex justify-content-center">
                      <span style={{
                        width: 44, height: 44, borderRadius: '50%',
                        background: i === 0 ? 'rgba(245, 158, 11, 0.15)' : i === 1 ? 'rgba(148, 163, 184, 0.15)' : 'rgba(205, 124, 47, 0.15)',
                        color: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : '#cd7c2f',
                        fontWeight: 800, fontSize: 18,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        #{i + 1}
                      </span>
                    </div>
                    <div className="fw-bold fs-6 mt-2">{a.agentName}</div>
                    <div className="text-muted small">{a.monthlyCarsSold} cars this month</div>
                    <div className="small text-success fw-semibold">{fmt(a.totalProfit)} profit</div>
                  </Card>
                </div>
              ))}
            </div>
          )}

          {/* Full leaderboard */}
          <Table hover responsive className="align-middle" style={{ fontSize: 13 }}>
            <thead><tr><th>#</th><th>Name</th><th>Role</th><th>Cars (Month)</th><th>Lifetime</th><th>Revenue</th><th>Profit</th></tr></thead>
            <tbody>
              {data.map((a,i) => (
                <tr key={a.agentId}>
                  <td className="fw-bold">#{i + 1}</td>
                  <td className="fw-semibold">{a.agentName}</td>
                  <td><Badge bg="secondary">{a.role}</Badge></td>
                  <td>{a.monthlyCarsSold}</td>
                  <td>{a.lifetimeCarsSold}</td>
                  <td>{fmt(a.totalRevenue)}</td>
                  <td className="text-success fw-semibold">{fmt(a.totalProfit)}</td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">No performance data yet.</td></tr>}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [brands,    setBrands]    = useState([]);

  useEffect(() => {
    api.get('/employees').then(r => setEmployees(Array.isArray(r.data)?r.data:[])).catch(()=>{});
    api.get('/brands').then(r => setBrands(Array.isArray(r.data)?r.data:[])).catch(()=>{});
  }, []);

  return (
    <div className="container-fluid px-2 py-1">
      <div className="text-muted small mb-3">Generate attendance, sales, payroll, and performance reports across all teams.</div>

      <Tab.Container defaultActiveKey="attendance">
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-transparent border-bottom-0 pt-3 px-3">
            <Nav variant="tabs">
              <Nav.Item><Nav.Link eventKey="attendance" className="d-inline-flex align-items-center gap-2"><Calendar size={15} /> Attendance</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="sales" className="d-inline-flex align-items-center gap-2"><Car size={15} /> Sales</Nav.Link></Nav.Item>
              {(user?.role === 'Admin' || user?.role === 'Manager') && <Nav.Item><Nav.Link eventKey="payroll" className="d-inline-flex align-items-center gap-2"><DollarSign size={15} /> Payroll</Nav.Link></Nav.Item>}
              {(user?.role === 'Admin' || user?.role === 'Manager') && <Nav.Item><Nav.Link eventKey="performance" className="d-inline-flex align-items-center gap-2"><Trophy size={15} /> Performance</Nav.Link></Nav.Item>}
            </Nav>
          </Card.Header>
          <Card.Body>
            <Tab.Content>
              <Tab.Pane eventKey="attendance"><AttendanceReport user={user} employees={employees} /></Tab.Pane>
              <Tab.Pane eventKey="sales"><SalesReport user={user} brands={brands} /></Tab.Pane>
              <Tab.Pane eventKey="payroll"><PayrollReport user={user} employees={employees} /></Tab.Pane>
              <Tab.Pane eventKey="performance"><PerformanceReport /></Tab.Pane>
            </Tab.Content>
          </Card.Body>
        </Card>
      </Tab.Container>
    </div>
  );
}
